/* Emissões → Planilha personalizada: a planilha do cliente como ela está. A análise sugere
   a categoria e a coluna de cada campo (pelo cabeçalho e pelo conteúdo); o usuário confere,
   corrige valores que não batem com as listas e importa. */

import { analisarColunas, itensDoCampo, lerValor, sugerirCategorias, valoresDaColuna, type Leitura } from '../../core/analisePlanilha';
import { carregarXlsx } from '../../core/arquivos';
import { catBySlug } from '../../core/catalogo';
import { cat, state } from '../../core/estado';
import { esc } from '../../core/formato';
import type { Campo, Categoria } from '../../tipos/api';
import { opcoesCategorias, temPadrao } from '../../ui/campos';
import { escLabel } from '../../ui/componentes';
import { ic } from '../../ui/dom';
import { toast } from '../../ui/toast';
import { render } from '../../roteador';
import type { Pagina } from '../pagina';
import { enviarImportacao, exemploCampo, lerPlanilha, resultadoHtml } from './importacao';

const FIXO = '__fixo__';
const AMOSTRA_PREVIA = 8;
const MAX_VALORES_CONFERIR = 40;

const ehLista = (x: Campo) => x.tipo === 'obra' || x.tipo.startsWith('lista:');
const chave = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v ?? '').trim());

interface LinhaValidada {
  n: number;
  dados: Record<string, unknown>;
  erros: string[];
}

/** Leituras de cada valor distinto, por campo (a mesma descrição aparece em muitas linhas). */
let memo = new Map<string, Leitura>();

function ler(x: Campo, bruto: unknown): Leitura {
  const p = state.pers;
  const corrigido = ehLista(x) ? p.depara[x.nome]?.[chave(bruto)] : undefined;
  if (corrigido) {
    const item = itensDoCampo(x, cat()).find((i) => String(i.id) === corrigido);
    if (item) return { ok: true, valor: item.id, exibicao: item.nome };
  }
  const k = `${x.nome}\u0000${chave(bruto)}`;
  let r = memo.get(k);
  if (!r) memo.set(k, (r = lerValor(x, bruto, cat())));
  return r;
}

function brutoDe(x: Campo, l: unknown[], indice: Record<string, number>): unknown {
  const fonte = state.pers.mapa[x.nome];
  if (fonte === FIXO) return state.pers.fixos[x.nome];
  return fonte ? l[indice[fonte]] : undefined;
}

function validar(c: Categoria): LinhaValidada[] {
  const p = state.pers.planilha;
  if (!p) return [];
  const indice = Object.fromEntries(p.cabecalho.map((h, i) => [h, i]));
  return p.rows.map((r) => {
    const dados: Record<string, unknown> = {};
    const erros: string[] = [];
    c.campos.forEach((x) => {
      const leitura = ler(x, brutoDe(x, r.l, indice));
      if (!leitura.ok) erros.push(`${x.rotulo}: ${leitura.erro}`);
      else if (leitura.valor !== null && leitura.valor !== undefined) dados[x.nome] = leitura.valor;
    });
    return { n: r.n, dados, erros };
  });
}

function seloConfianca(x: Campo): string {
  const p = state.pers;
  const s = p.analise[x.nome];
  if (p.mapa[x.nome] === FIXO) return '<span class="pill grey">valor fixo</span>';
  if (!p.mapa[x.nome]) return x.obrigatorio && !temPadrao(x) ? '<span class="pill erro">sem coluna</span>' : '';
  if (!s || s.coluna !== p.mapa[x.nome]) return '<span class="pill grey">escolhida por você</span>';
  const [classe, texto] = s.confianca >= 0.7 ? ['sucesso', 'alta'] : s.confianca >= 0.45 ? ['warnp', 'média'] : ['erro', 'baixa'];
  return `<span class="pill ${classe}" title="${esc(s.motivo)}">confiança ${texto}</span>`;
}

function inputFixo(x: Campo): string {
  const v = state.pers.fixos[x.nome] ?? '';
  const atributos = `class="inp" data-muda="fixo" data-campo="${x.nome}" style="margin-top:8px"`;
  if (ehLista(x)) {
    return `<select ${atributos}><option value="">Selecione…</option>${itensDoCampo(x, cat()).map((i) =>
      `<option value="${esc(i.id)}" ${String(i.id) === v ? 'selected' : ''}>${esc(i.nome)}</option>`).join('')}</select>`;
  }
  if (x.tipo === 'opcoes') {
    return `<select ${atributos}><option value="">Selecione…</option>${x.opcoes.map((o) => `<option ${o === v ? 'selected' : ''}>${esc(o)}</option>`).join('')}</select>`;
  }
  return `<input ${atributos} ${x.tipo === 'data' ? 'type="date"' : ''} value="${esc(v)}" placeholder="${esc(exemploCampo(x) instanceof Date ? '' : exemploCampo(x))}">`;
}

function colunasHtml(c: Categoria): string {
  const p = state.pers;
  const colunas = (p.planilha?.cabecalho || []).filter(Boolean);
  return c.campos.map((x) => {
    const fonte = p.mapa[x.nome] || '';
    const s = p.analise[x.nome];
    return `<div class="map-row" style="align-items:start">
      <div><b>${esc(x.rotulo)}${x.obrigatorio ? ' <span class="req">*</span>' : ''}</b>
        <small>${temPadrao(x) && !fonte ? `Sem coluna: usa ${esc(x.padrao)}` : s?.coluna === fonte && fonte ? esc(s.motivo) : esc(x.dica)}</small></div>
      <div>
        <div class="row" style="gap:8px;flex-wrap:nowrap">
          <select class="inp" data-muda="coluna" data-campo="${x.nome}">
            <option value="">— não importar —</option>
            ${colunas.map((h) => `<option value="${esc(h)}" ${h === fonte ? 'selected' : ''}>${esc(h)}</option>`).join('')}
            <option value="${FIXO}" ${fonte === FIXO ? 'selected' : ''}>Mesmo valor para todas as linhas…</option>
          </select>${seloConfianca(x)}
        </div>
        ${fonte === FIXO ? inputFixo(x) : ''}
      </div></div>`;
  }).join('');
}

/** Valores das colunas de lista que não batem exatamente com o catálogo: para conferir ou corrigir. */
function conferirHtml(c: Categoria): string {
  const p = state.pers;
  if (!p.planilha) return '';
  const blocos = c.campos.filter((x) => ehLista(x) && p.mapa[x.nome] && p.mapa[x.nome] !== FIXO).map((x) => {
    const contagem = new Map<string, number>();
    valoresDaColuna(p.planilha!, p.mapa[x.nome]).forEach((v) => {
      const k = chave(v);
      if (k) contagem.set(k, (contagem.get(k) || 0) + 1);
    });
    const itens = itensDoCampo(x, cat());
    const linhas = [...contagem].map(([valor, n]) => ({ valor, n, leitura: lerValor(x, valor, cat()) }))
      .filter(({ leitura }) => !leitura.ok || leitura.aproximado)
      .sort((a, b) => Number(a.leitura.ok) - Number(b.leitura.ok) || b.n - a.n);
    if (!linhas.length) return '';
    const mostradas = linhas.slice(0, MAX_VALORES_CONFERIR);
    return `<h3 style="font-size:14px;margin:18px 0 6px">${esc(x.rotulo)}</h3>
      ${mostradas.map(({ valor, n, leitura }) => {
        const escolhido = p.depara[x.nome]?.[valor] ?? (leitura.ok ? String(leitura.valor) : '');
        const sugestoes = !leitura.ok ? leitura.sugestoes || [] : [];
        return `<div class="map-row">
          <div><b>${esc(valor)}</b><small>${n} linha(s) · ${leitura.ok ? 'lido por semelhança — confira' : '<span style="color:var(--erro-fg)">não identificado</span>'}</small></div>
          <select class="inp ${!leitura.ok && !escolhido ? 'err' : ''}" data-muda="depara" data-campo="${x.nome}" data-valor="${esc(valor)}">
            <option value="">Selecione…</option>
            ${sugestoes.length ? `<optgroup label="Mais parecidos">${sugestoes.map((i) => `<option value="${esc(i.id)}" ${String(i.id) === escolhido ? 'selected' : ''}>${esc(i.nome)}</option>`).join('')}</optgroup>` : ''}
            <optgroup label="Todos">${itens.map((i) => `<option value="${esc(i.id)}" ${String(i.id) === escolhido && !sugestoes.some((s) => s.id === i.id) ? 'selected' : ''}>${esc(i.nome)}</option>`).join('')}</optgroup>
          </select></div>`;
      }).join('')}
      ${linhas.length > mostradas.length ? `<small style="color:var(--muted)">E mais ${linhas.length - mostradas.length} valor(es).</small>` : ''}`;
  }).join('');
  return blocos ? `<h2 class="sec" style="margin-top:28px">4. Confira os valores das listas</h2>
    <p style="color:var(--muted);font-size:13px;margin:0">Valores que não estão escritos como no catálogo. Os lidos por semelhança já vêm escolhidos; os não identificados precisam de uma escolha (ou a linha fica de fora).</p>${blocos}` : '';
}

function previaHtml(c: Categoria, linhas: LinhaValidada[]): string {
  const p = state.pers;
  const campos = c.campos.filter((x) => p.mapa[x.nome] || temPadrao(x));
  if (!campos.length || !p.planilha) return '';
  const indice = Object.fromEntries(p.planilha.cabecalho.map((h, i) => [h, i]));
  const corpo = p.planilha.rows.slice(0, AMOSTRA_PREVIA).map((r) => `<tr><td class="num">${r.n}</td>${campos.map((x) => {
    const leitura = ler(x, brutoDe(x, r.l, indice));
    if (!leitura.ok) return `<td style="color:var(--erro-fg)" title="${esc(leitura.erro)}">⚠ ${esc(leitura.erro)}</td>`;
    return `<td ${leitura.aproximado ? 'title="Lido por semelhança"' : ''}>${leitura.aproximado ? '≈ ' : ''}${esc(leitura.exibicao)}</td>`;
  }).join('')}</tr>`).join('');
  const invalidas = linhas.filter((l) => l.erros.length);
  return `<h2 class="sec" style="margin-top:28px">Pré-visualização</h2>
    <div class="table-wrap" style="overflow-x:auto"><table class="table" style="font-size:12.5px">
      <thead><tr><th>Linha</th>${campos.map((x) => `<th>${esc(x.rotulo)}</th>`).join('')}</tr></thead><tbody>${corpo}</tbody></table></div>
    ${p.planilha.rows.length > AMOSTRA_PREVIA ? `<small style="color:var(--muted)">Primeiras ${AMOSTRA_PREVIA} de ${p.planilha.rows.length} linhas.</small>` : ''}
    ${invalidas.length ? `<div class="callout warn" style="margin-top:14px">${ic('i-alert')}<div><b>${invalidas.length} linha(s) com problema.</b><br>
      ${invalidas.slice(0, 5).map((l) => `Linha ${l.n}: ${esc(l.erros.join('; '))}`).join('<br>')}${invalidas.length > 5 ? '<br>…' : ''}
      <label style="display:flex;gap:8px;align-items:center;margin-top:10px;font-weight:600"><input type="checkbox" data-muda="pular" ${p.pularInvalidas ? 'checked' : ''}> Importar só as ${linhas.length - invalidas.length} linha(s) sem problema</label></div></div>` : ''}`;
}

function sugestaoCategoriaHtml(c: Categoria): string {
  const p = state.pers;
  if (!p.planilha) return '';
  const [melhor] = sugerirCategorias(p.planilha, cat());
  const atual = sugerirCategorias(p.planilha, cat()).find((s) => s.slug === c.slug);
  if (!melhor || melhor.slug === c.slug || melhor.pontos < 0.45 || melhor.pontos - (atual?.pontos || 0) < 0.15) return '';
  return `<div class="callout info" style="margin-top:12px">${ic('i-info')}<div>As colunas parecem ser de <b>${esc(melhor.nome)}</b>.
    <button class="btn-plain" style="font-weight:700;text-decoration:underline" data-clique="usarCategoria" data-slug="${melhor.slug}">Usar esta categoria</button></div></div>`;
}

/** Refaz a análise (planilha nova ou outra categoria). */
function analisar(c: Categoria): void {
  const p = state.pers;
  memo = new Map();
  p.depara = {};
  p.analise = p.planilha ? analisarColunas(c, p.planilha, cat()) : {};
  p.mapa = Object.fromEntries(Object.entries(p.analise).filter(([, s]) => s.coluna).map(([campo, s]) => [campo, s.coluna as string]));
  p.resultado = null;
}

export const personalizada: Pagina = {
  render() {
    const p = state.pers;
    const c = catBySlug(p.slug);
    p.slug = c.slug;
    const linhas = validar(c);
    const validas = linhas.filter((l) => !l.erros.length).length;
    const podeImportar = validas > 0 && (validas === linhas.length || p.pularInvalidas);
    return `
  <h1 class="page">Planilha personalizada</h1>
  <p class="sub">Envie a planilha que você já tem, com as colunas do seu jeito. A ZNIT identifica a categoria e qual coluna corresponde a cada campo — você só confere.</p>
  <div class="grid g2" style="align-items:start;grid-template-columns:minmax(0,1.5fr) minmax(0,1fr)">
    <div class="card">
      <h2 class="sec">1. Sua planilha</h2>
      <div class="drop ${p.planilha ? 'has-files' : ''}" data-clique="escolher">
        ${ic('i-upload')}<div><u>Envie</u> a planilha aqui</div><span class="fmt">XLSX ou XLSM — até 20 MB</span>
      </div>
      <input type="file" id="filePers" accept=".xlsx,.xlsm" style="display:none" data-muda="arquivo">
      ${p.planilha ? `<div class="file-chip" style="margin-top:12px">${ic('i-doc')}${esc(p.planilha.arquivo)}
        <small>aba "${esc(p.planilha.aba)}" · cabeçalho na linha ${p.planilha.linhaCab} · ${p.planilha.rows.length} linha(s)</small></div>` : ''}
      ${p.planilha ? `
      <h2 class="sec" style="margin-top:28px">2. Categoria</h2>
      <div class="field"><select class="inp" data-muda="categoria">${opcoesCategorias(c.slug)}</select>
        <div class="hint">${escLabel(c.escopo)} · ${esc(c.descricao)}</div></div>
      ${sugestaoCategoriaHtml(c)}
      <h2 class="sec" style="margin-top:28px">3. Colunas</h2>
      <p style="color:var(--muted);font-size:13px;margin:0 0 8px">Sugeridas pelo nome e pelo conteúdo de cada coluna. Troque o que precisar; campos sem coluna podem usar o mesmo valor em todas as linhas.</p>
      ${colunasHtml(c)}
      ${conferirHtml(c)}
      ${previaHtml(c, linhas)}
      <button class="btn btn-primary ${podeImportar ? '' : 'dis'}" data-clique="importar" style="margin-top:18px">${ic('i-upload')} Importar ${p.pularInvalidas ? validas : linhas.length} linha(s)</button>` : ''}
    </div>
    <div style="display:flex;flex-direction:column;gap:16px">
      ${resultadoHtml(p.resultado)}
      <div class="callout info">${ic('i-info')}
        <div><b>Como a análise funciona?</b><br>Cada campo da categoria é comparado com as colunas da planilha: pelo nome do cabeçalho (inclusive nomes comuns como "Qtd", "Destinação", "Empreendimento") e pelo conteúdo — datas, números e valores que batem com as listas do catálogo, como tipos de resíduo, destinos e combustíveis.<br><br>
        Valores escritos de outro jeito ("Papelão", "aterro sanitário") são lidos por semelhança e aparecem para você conferir. Nada é gravado antes de você clicar em Importar.</div>
      </div>
      <div class="callout neutral">${ic('i-info')}<div>Planilha com várias categorias misturadas ou fora de qualquer padrão? Envie em <a class="btn-plain" style="font-weight:700;text-decoration:underline" href="#/emissoes/documentos">Processar documentos</a> que a equipe ZNIT trata para você.</div></div>
    </div>
  </div>`;
  },

  acoes: {
    escolher: () => document.getElementById('filePers')?.click(),

    async arquivo(el) {
      const file = (el as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const p = state.pers;
        Object.assign(p, { planilha: await lerPlanilha(file), file, fixos: {}, pularInvalidas: false });
        // Sem categoria escolhida antes, começa pela que mais combina com as colunas.
        const [melhor] = sugerirCategorias(p.planilha!, cat());
        if (melhor && melhor.pontos >= 0.45) p.slug = melhor.slug;
        analisar(catBySlug(p.slug));
        render();
      } catch (e) {
        toast((e as Error).message || 'Não foi possível ler a planilha.');
      }
    },

    categoria(el) {
      state.pers.slug = (el as HTMLSelectElement).value;
      analisar(catBySlug(state.pers.slug));
      render();
    },

    usarCategoria(el) {
      state.pers.slug = el.dataset.slug || null;
      analisar(catBySlug(state.pers.slug));
      render();
    },

    coluna(el) {
      state.pers.mapa[el.dataset.campo as string] = (el as HTMLSelectElement).value;
      render();
    },

    fixo(el) {
      state.pers.fixos[el.dataset.campo as string] = (el as HTMLInputElement).value;
      render();
    },

    depara(el) {
      const campo = el.dataset.campo as string;
      (state.pers.depara[campo] ||= {})[el.dataset.valor as string] = (el as HTMLSelectElement).value;
      render();
    },

    pular(el) {
      state.pers.pularInvalidas = (el as HTMLInputElement).checked;
      render();
    },

    async importar(el) {
      const p = state.pers;
      const c = catBySlug(p.slug);
      if (!p.planilha) return;
      const linhas = validar(c);
      const validas = linhas.filter((l) => !l.erros.length);
      if (!validas.length) return toast('Nenhuma linha pronta para importar.');
      if (validas.length < linhas.length && !p.pularInvalidas) return toast('Corrija as linhas com problema ou marque para importar só as linhas sem problema.');

      // O backend recebe uma planilha gerada aqui, já com os valores lidos (IDs das listas).
      const campos = c.campos.map((x) => x.nome).filter((nome) => validas.some((l) => l.dados[nome] !== undefined));
      const XLSX = await carregarXlsx();
      const ws = XLSX.utils.aoa_to_sheet([campos, ...validas.map((l) => campos.map((nome) => l.dados[nome] ?? null))]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Lancamentos');
      const blob = new Blob([XLSX.write(wb, { type: 'array', bookType: 'xlsx' })], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      el.classList.add('dis');
      p.resultado = await enviarImportacao({
        categoria: c, arquivo: p.planilha.arquivo, file: blob,
        mapa: Object.fromEntries(campos.map((nome) => [nome, nome])), fixos: {},
        aba: 'Lancamentos', linhaCab: 1,
        linhas: validas.map((l) => ({ dados: l.dados, n: l.n })),
        linhaOriginal: (linha) => validas[linha - 2]?.n ?? linha,
      });
      if (p.resultado.ok) toast(`${p.resultado.n} lançamento(s) importado(s).`);
      render();
    },
  },
};
